import React from 'react';

/**
 * A simple SVG gauge showing where the forecast falls on a drier ↔ wetter spectrum.
 * The needle position is driven by percent-of-normal (100% = center/normal).
 */

const GAUGE_W = 200;
const CENTER_X = GAUGE_W / 2;
const CENTER_Y = 110;
const RADIUS = 80;
const END_LABEL_Y = CENTER_Y + 14;
const GAUGE_H = END_LABEL_Y + 4;

// Map percent-of-normal to angle. 50% → full left, 100% → center, 150% → full right.
// Clamped to [50, 150].
function pctToAngle(pct: number): number {
  const clamped = Math.max(50, Math.min(150, pct));
  // Map 50→π (left), 100→π/2 (top), 150→0 (right)
  return Math.PI * (1 - (clamped - 50) / 100);
}

function arcPath(startAngle: number, endAngle: number, r: number): string {
  const x1 = CENTER_X + r * Math.cos(Math.PI - startAngle);
  const y1 = CENTER_Y - r * Math.sin(Math.PI - startAngle);
  const x2 = CENTER_X + r * Math.cos(Math.PI - endAngle);
  const y2 = CENTER_Y - r * Math.sin(Math.PI - endAngle);
  const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
  return `M ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2}`;
}

const BANDS: Array<{start: number; end: number; color: string; label: string}> = [
  {start: 50, end: 75, color: '#dc2626', label: 'Well Below'},
  {start: 75, end: 90, color: '#f97316', label: 'Below'},
  {start: 90, end: 110, color: '#84cc16', label: 'Normal'},
  {start: 110, end: 125, color: '#06b6d4', label: 'Above'},
  {start: 125, end: 150, color: '#2563eb', label: 'Well Above'},
];

type Props = {
  percentOfNormal: number;
  label?: string;
};

export default function OutlookGauge({percentOfNormal, label}: Props) {
  const angle = pctToAngle(percentOfNormal);
  const needleLen = RADIUS - 8;
  const needleX = CENTER_X + needleLen * Math.cos(angle);
  const needleY = CENTER_Y - needleLen * Math.sin(angle);

  return (
    <div className="flex flex-col items-center">
      <svg width={GAUGE_W} height={GAUGE_H} viewBox={`0 0 ${GAUGE_W} ${GAUGE_H}`}>
        {/* Color bands */}
        {BANDS.map((band) => {
          const a1 = (band.start - 50) / 100; // 0→1 normalized
          const a2 = (band.end - 50) / 100;
          return (
            <path
              key={band.label}
              d={arcPath(a1 * Math.PI, a2 * Math.PI, RADIUS)}
              fill="none"
              stroke={band.color}
              strokeWidth={14}
              strokeLinecap="butt"
              opacity={0.7}
            />
          );
        })}

        {/* Needle */}
        <line
          x1={CENTER_X}
          y1={CENTER_Y}
          x2={needleX}
          y2={needleY}
          stroke="#1f2937"
          strokeWidth={2.5}
          strokeLinecap="round"
        />
        <circle cx={CENTER_X} cy={CENTER_Y} r={4} fill="#1f2937" />

        {/* Labels */}
        <text x={CENTER_X - RADIUS} y={END_LABEL_Y} fontSize={9} fill="#6b7280" textAnchor="middle">
          Drier
        </text>
        <text x={CENTER_X + RADIUS} y={END_LABEL_Y} fontSize={9} fill="#6b7280" textAnchor="middle">
          Wetter
        </text>
      </svg>
      <div className="text-center -mt-1">
        <span className="text-lg font-bold">{Math.round(percentOfNormal)}%</span>
        <span className="text-xs text-gray-500 ml-1">of normal</span>
      </div>
      {label && <p className="text-xs text-gray-500 mt-0.5">{label}</p>}
    </div>
  );
}
